import { useMemo, useState, useEffect } from 'react'
import { Stack, MenuItem, Box, Select, InputLabel, FormControl, Button, Skeleton, Typography } from '@mui/material'

import { i18WithDefault as t } from '@shared/locales'
import { DEFAULT_TIMEZONE } from '@shared/dates'
import { formatCycleLabel } from './helper'
import { TBenefitConsumptionTableFilters, TMembershipCycle } from './types'

const ALL_OPTION = t('global.label.all')
const NONE_OPTION = t('global.label.none')

const BenefitFilter = ({
  selectedBenefitOffer,
  setSelectedBenefitOffer,
  benefitOffers,
  hideBenefitFilter,
  inline = false,
}: {
  selectedBenefitOffer: string
  setSelectedBenefitOffer: (value: string) => void
  benefitOffers: Map<string, string>
  hideBenefitFilter: boolean
  inline?: boolean
}) => {
  if (hideBenefitFilter) return null

  return (
    <FormControl sx={{ width: { xs: '100%', sm: inline ? 200 : 250 } }}>
      <InputLabel id="benefitOffer-label">{t('global.label.benefit')}</InputLabel>
      <Select
        sx={{ backgroundColor: 'white' }}
        name="benefitOffer"
        labelId="benefitOffer-label"
        label={t('global.label.benefit')}
        value={selectedBenefitOffer}
        onChange={(e) => setSelectedBenefitOffer(e.target.value)}
      >
        <MenuItem value={ALL_OPTION}>{ALL_OPTION}</MenuItem>
        {Array.from(benefitOffers.keys()).map((offerId) => (
          <MenuItem key={offerId} value={offerId}>
            {benefitOffers.get(offerId)} {t('membership.benefit.discount.small')}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

const YearFilter = ({
  selectedYear,
  setSelectedYear,
  selectedCycle,
  setSelectedCycle,
  years,
  cycles,
  inline = false,
}: {
  selectedYear: string
  setSelectedYear: (year: string) => void
  selectedCycle: TMembershipCycle | undefined
  setSelectedCycle: (cycle: TMembershipCycle | undefined) => void
  years: string[]
  cycles: Map<string, TMembershipCycle[]>
  inline?: boolean
}) => {
  const visibleYears = useMemo(() => years.filter((year) => cycles.get(year)?.length), [years, cycles])
  return (
    <FormControl sx={{ width: { xs: '100%', sm: inline ? 110 : 200 } }} disabled={!selectedCycle}>
      <InputLabel id="year-label">{t('global.label.year')}</InputLabel>
      <Select
        sx={{ backgroundColor: 'white' }}
        name="year"
        labelId="year-label"
        label={t('global.label.year')}
        value={selectedCycle ? selectedYear : NONE_OPTION}
        onChange={(e) => {
          setSelectedYear(e.target.value)
          setSelectedCycle(cycles.get(e.target.value)?.[0])
        }}
      >
        {!selectedCycle && (
          <MenuItem value={NONE_OPTION} disabled>
            -
          </MenuItem>
        )}
        {visibleYears.map((year, idx) => (
          <MenuItem key={`${year}_${idx}`} value={year}>
            {year}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

const CycleFilter = ({
  selectedYear,
  selectedCycle,
  setSelectedCycle,
  cycles,
  inline = false,
  currentCycle,
  baseCycle,
  timezone = DEFAULT_TIMEZONE,
}: {
  selectedYear: string
  selectedCycle: TMembershipCycle | undefined
  setSelectedCycle: (cycle: TMembershipCycle | undefined) => void
  cycles: Map<string, TMembershipCycle[]>
  currentCycle: TMembershipCycle
  baseCycle: TMembershipCycle
  timezone?: string
  inline?: boolean
}) => {
  const visibleCycles = useMemo(() => cycles.get(selectedYear) || [], [cycles, selectedYear])

  const value = useMemo(() => {
    if (!visibleCycles.length) return NONE_OPTION
    return selectedCycle ? formatCycleLabel(selectedCycle, timezone) : ALL_OPTION
  }, [selectedCycle, visibleCycles.length, timezone])

  return (
    <FormControl sx={{ width: { xs: '100%', sm: inline ? 260 : 360 } }}>
      <InputLabel id="cycle-label">{t('global.label.membershipCycle')}</InputLabel>
      <Select
        sx={{ backgroundColor: 'white' }}
        name="cycle"
        labelId="cycle-label"
        label={t('global.label.membershipCycle')}
        value={value}
        onChange={(e) => {
          if ([ALL_OPTION, NONE_OPTION].includes(e.target.value)) {
            setSelectedCycle(undefined)
            return
          }
          const cycle = visibleCycles.find((cycle) => formatCycleLabel(cycle, timezone) === e.target.value)
          if (!cycle) return
          setSelectedCycle(cycle)
        }}
      >
        {!visibleCycles?.length && (
          <MenuItem key="no-cycle" value={NONE_OPTION} disabled>
            {t('membership.benefit.noCycleFound')}
          </MenuItem>
        )}
        {visibleCycles?.length && (
          <MenuItem key="all-cycles" value={ALL_OPTION}>
            {t('membership.benefit.allCycles')}
          </MenuItem>
        )}
        {visibleCycles?.map((cycle, idx) => {
          const currentCycleLabel = formatCycleLabel(cycle, timezone)
          const isCurrentCycle = currentCycleLabel === formatCycleLabel(baseCycle, timezone)
          return (
            <MenuItem key={`${cycle}_${idx}`} value={currentCycleLabel}>
              <Stack direction="row" alignItems="center" spacing={1}>
                {isCurrentCycle && (
                  <Typography color="success.dark" variant="body1">
                    {t('membership.benefit.current')}
                  </Typography>
                )}
                <Typography variant="body1" noWrap>
                  {currentCycleLabel}
                </Typography>
              </Stack>
            </MenuItem>
          )
        })}
      </Select>
    </FormControl>
  )
}

export const BenefitConsumptionTableFilters = ({
  benefitOffers,
  cycles = new Map<string, TMembershipCycle[]>(),
  defaultCycle,
  baseCycle,
  years,
  defaultYear,
  onApplyFilters,
  onClearFilters,
  hideBenefitFilter,
  inline = false,
  isLoading = false,
  timezone = DEFAULT_TIMEZONE,
}: {
  benefitOffers: Map<string, string>
  cycles: Map<string, TMembershipCycle[]>
  defaultCycle?: TMembershipCycle
  baseCycle?: TMembershipCycle
  years: string[]
  defaultYear?: string
  onApplyFilters: (filters: TBenefitConsumptionTableFilters) => void
  onClearFilters: () => void
  hideBenefitFilter?: boolean
  inline?: boolean
  isLoading?: boolean
  timezone?: string
}) => {
  const [selectedBenefitOffer, setSelectedBenefitOffer] = useState<string>(ALL_OPTION)
  const [selectedYear, setSelectedYear] = useState<string>(defaultYear || '')
  const [selectedCycle, setSelectedCycle] = useState<TMembershipCycle | undefined>(defaultCycle)

  const visibleYears = useMemo(() => {
    if (!years?.length) {
      return [defaultYear || '']
    }
    return years
  }, [years, defaultYear])

  const disableButtons = useMemo(() => {
    return !cycles.get(selectedYear)?.length
  }, [selectedYear, cycles])

  useEffect(() => {
    if (defaultCycle) {
      setSelectedCycle(defaultCycle)
      setSelectedYear(defaultYear || '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCycle])

  const handleApplyFilters = () => {
    onApplyFilters({
      benefitOffer: selectedBenefitOffer,
      year: selectedYear,
      cycle: selectedCycle,
    })
  }

  const resetFilters = () => {
    setSelectedBenefitOffer(ALL_OPTION)
    setSelectedYear(defaultYear || '')
    setSelectedCycle(defaultCycle)
    onClearFilters()
  }

  const benefitFilter = (
    <BenefitFilter
      selectedBenefitOffer={selectedBenefitOffer}
      setSelectedBenefitOffer={setSelectedBenefitOffer}
      benefitOffers={benefitOffers}
      hideBenefitFilter={hideBenefitFilter || false}
      inline={inline}
    />
  )

  const yearFilter = (
    <YearFilter
      selectedYear={selectedYear}
      setSelectedYear={setSelectedYear}
      selectedCycle={selectedCycle}
      setSelectedCycle={setSelectedCycle}
      years={visibleYears}
      inline={inline}
      cycles={cycles}
    />
  )

  const cycleFilter = (
    <CycleFilter
      selectedYear={selectedYear}
      selectedCycle={selectedCycle}
      setSelectedCycle={setSelectedCycle}
      currentCycle={defaultCycle}
      baseCycle={baseCycle}
      cycles={cycles}
      inline={inline}
      timezone={timezone}
    />
  )

  if (!defaultCycle || !defaultYear) {
    return null
  }

  if (inline) {
    // Inline layout for use in table dropdownFilters
    return (
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent={{ xs: 'flex-start', sm: 'space-between' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
          {isLoading ? (
            <>
              <Skeleton width={260} height={55} variant="rounded" />
              <Skeleton width={100} height={55} variant="rounded" />
              <Skeleton width={360} height={55} variant="rounded" />
              <Skeleton width={90} height={55} variant="rounded" />
              <Skeleton width={90} height={55} variant="rounded" />
            </>
          ) : (
            <>
              {benefitFilter}
              {yearFilter}
              {cycleFilter}
            </>
          )}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: { xs: 1, sm: 0 } }}>
          <Button disabled={disableButtons} onClick={resetFilters} color="primary" sx={{ px: 3 }}>
            {t('global.label.clear')}
          </Button>
          <Button disabled={disableButtons} variant="contained" onClick={handleApplyFilters} sx={{ px: 3 }}>
            {t('global.label.search')}
          </Button>
        </Stack>
      </Stack>
    )
  }

  // Default layout for standalone use
  return (
    <Box>
      <Stack spacing={2} overflow="auto">
        <Stack direction="row" spacing={1} alignItems="center" pt={2}>
          {isLoading ? (
            <>
              <Skeleton width={260} height={55} variant="rounded" />
              <Skeleton width={100} height={55} variant="rounded" />
              <Skeleton width={360} height={55} variant="rounded" />
              <Skeleton width={90} height={55} variant="rounded" />
              <Skeleton width={90} height={55} variant="rounded" />
            </>
          ) : (
            <Stack direction="row" spacing={2} alignItems="center">
              <>
                {benefitFilter}
                {yearFilter}
                {cycleFilter}
              </>
              <Button variant="text" onClick={resetFilters} color="primary" sx={{ py: 1, px: 2 }}>
                {t('global.label.clear')}
              </Button>
              <Button
                variant="contained"
                onClick={handleApplyFilters}
                color="primary"
                sx={{ py: 1, px: 2 }}
                disabled={disableButtons}
              >
                {t('global.label.apply')}
              </Button>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Box>
  )
}
